import { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Paperclip, Mic, Plus, Loader2, History, X, Menu, MessageSquare, ChevronDown, Pin, Lightbulb, RefreshCw, Zap, Layout, FileCheck, ChevronLeft, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { UserMenu } from "@/components/UserMenu";
import { ClarityIndicator } from "@/components/session/ClarityIndicator";
import { SessionHistory } from "@/components/session/SessionHistory";
import { ProgressTracker } from "@/components/session/ProgressTracker";
import { SubphaseTransitionDialog } from "@/components/session/SubphaseTransitionDialog";
import { Phase2Transition } from "@/components/session/Phase2Transition";
import { SaveStatusIndicator } from "@/components/session/SaveStatusIndicator";
import { PhaseTimeline } from "@/components/session/PhaseTimeline";
import { DecisionFrameworkSummary } from "@/components/session/DecisionFrameworkSummary";
import { ScenarioBuilder } from "@/components/session/ScenarioBuilder";
import { ActiveUsers } from "@/components/session/ActiveUsers";
import { CollaborationBar } from "@/components/session/CollaborationBar";
import { SessionComments } from "@/components/session/SessionComments";
import { SessionActivityTimeline } from "@/components/session/SessionActivityTimeline";
import { ParticipantsPanel } from "@/components/session/ParticipantsPanel";
import { useSessionParticipants } from "@/hooks/useSessionParticipants";
// OnboardingContextPanel removed - now using collapsible inline
import { HorizontalPhaseIndicator } from "@/components/session/HorizontalPhaseIndicator";
import { GlobalPhaseTimeline } from "@/components/session/GlobalPhaseTimeline";
import { PhaseNavigationDialog } from "@/components/session/PhaseNavigationDialog";
import { ResponseQualityIndicator } from "@/components/session/ResponseQualityIndicator";
import { StructuredInputHints } from "@/components/session/StructuredInputHints";
import { Phase11CommitGate, type Phase11Artifacts, type OnboardingContext } from "@/components/session/Phase11CommitGate";

import { Phase12ConflictInput } from "@/components/session/Phase12ConflictInput";
import { Phase12EscalateInput } from "@/components/session/Phase12EscalateInput";
import { Phase13IrreversibilityEngine, type Phase13Data } from "@/components/session/Phase13IrreversibilityEngine";
import { Phase14DecisionThesis } from "@/components/session/Phase14DecisionThesis";
import { useSessionSteps } from "@/hooks/useSessionSteps";
import { useStepDraft } from "@/hooks/useStepDraft";
import { useSessionPresence } from "@/hooks/useSessionPresence";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { logStartFlowEvent } from "@/lib/telemetry/startFlow";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import quentariLogo from "@/assets/quentari-logo.svg";

const Session = () => {
  const { sessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { activeUsers, updatePresence } = useSessionPresence(sessionId, user?.id || null);
  const { myRole } = useSessionParticipants(sessionId);
  
  const [thoughtContent, setThoughtContent] = useState("");
  const [structuredInputData, setStructuredInputData] = useState<Phase11Artifacts | null>(null);
  const [commitGateValid, setCommitGateValid] = useState(false);
  
  // Phase 1.2 structured conflict data (Resource-Allocation-Gate)
  const [conflictInputData, setConflictInputData] = useState<{
    capacityPercent: number;
    kpiMetric: string;
    kpiFrom: string;
    kpiTo: string;
    impactTimeframe: string;
    tensionScore: number;
    riskAlignmentScore: number;
    riskType: string;
  } | null>(null);
  const [conflictGateValid, setConflictGateValid] = useState(false);
  
  const [escalateGateValid, setEscalateGateValid] = useState(false);
  
  // Phase 1.3 structured irreversibility data
  const [phase13Data, setPhase13Data] = useState<Phase13Data | null>(null);
  const [phase13GateValid, setPhase13GateValid] = useState(false);
  
  const [conflictCostData, setConflictCostData] = useState<{
    delayMonths: number;
    monthlyCost: number;
    totalCost: number;
    wastedCapacityMonths: number;
    conversionRate: number;
    avgUnitValue: number;
    commitmentDecision: 'wait' | 'commit';
  } | null>(null);
  
  // Phase 1.4 killer assumption data
  const [killerAssumptionData, setKillerAssumptionData] = useState<{
    killerAssumption: string;
  } | null>(null);
  const [question, setQuestion] = useState("");
  const [paraphrase, setParaphrase] = useState("");
  const [questionId, setQuestionId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [phase, setPhase] = useState(1);
  const [subphase, setSubphase] = useState("1.1");
  const [clarityScore, setClarityScore] = useState(0);
  const [kernStatus, setKernStatus] = useState<'diffus' | 'in_bearbeitung' | 'klar'>('in_bearbeitung');
  const [detectedTensions, setDetectedTensions] = useState<string[]>([]);
  const [problemStatement, setProblemStatement] = useState("");
  
  // Session context for dynamic placeholders
  const [sessionContext, setSessionContext] = useState<{
    decision_type?: string;
    decision_subtype?: string;
    trigger?: string;
    stakes?: string;
  }>({});
  
  // Onboarding context for Phase 1.1 Commit Gate
  const [onboardingContext, setOnboardingContext] = useState<OnboardingContext>({});
  
  // Track follow-up type to show different input for escalate questions
  const [followUpType, setFollowUpType] = useState<'primary' | 'escalate'>('primary');
  
  const [analysis, setAnalysis] = useState<{
    summary?: string;
    fine_classification?: string;
    complexity?: string;
    meta?: any;
  } | null>(null);
  
  const [sessionMeta, setSessionMeta] = useState<any>(null);
  const [dynamicPlaceholders, setDynamicPlaceholders] = useState<string[]>([]);
  
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [editingStepNumber, setEditingStepNumber] = useState<number | null>(null);
  const [currentInternalName, setCurrentInternalName] = useState<string>("");
  const [answeredInSubphase, setAnsweredInSubphase] = useState(0);
  const [totalInSubphase, setTotalInSubphase] = useState(3);
  const [nextSubphase, setNextSubphase] = useState<string>("");
  const [showTransitionDialog, setShowTransitionDialog] = useState(false);
  const [showPhase2Transition, setShowPhase2Transition] = useState(false);
  const [transitionData, setTransitionData] = useState<{
    fromSubphase: string;
    toSubphase: string;
    tensions: string[];
    problemStatement: string;
    fromPhase?: number;
    toPhase?: number;
  } | null>(null);
  const { steps, isLoading: isLoadingSteps, markStepSeen, latestLiveStep, clearLatestLiveStep } = useSessionSteps(sessionId);
  
  // Typing indicator debounce
  const typingTimeoutRef = useRef<number | null>(null);
  
  // File attachment state
  const [attachments, setAttachments] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [rowVersion, setRowVersion] = useState<number>(0);
  const saveTimeoutRef = useRef<number | null>(null);

  // Draft autosave – keyed by session + subphase so text survives conflicts
  const { saveDraft, restoreDraft, clearDraft, copyDraftToClipboard } = useStepDraft(sessionId, subphase);
  
  // Comments state
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);

  // Global phase navigation state
  const [showGlobalPhaseDialog, setShowGlobalPhaseDialog] = useState(false);
  const [targetGlobalPhase, setTargetGlobalPhase] = useState<number | null>(null);

  // ═══════════════════════════════════════════
  // TELEMETRY: Track Phase 1.1 engagement
  // ═══════════════════════════════════════════
  const questionDisplayedRef = useRef<string | null>(null);
  const pageEnteredAtRef = useRef<number>(Date.now());
  const hasSubmittedRef = useRef(false);

  // Track question_displayed when question changes in Phase 1.1
  useEffect(() => {
    if (subphase === '1.1' && questionId && workspaceId && questionId !== questionDisplayedRef.current) {
      questionDisplayedRef.current = questionId;
      pageEnteredAtRef.current = Date.now();
      const dimension = sessionMeta?.onboarding_context?.dominant_dimension || 'unknown';
      logStartFlowEvent({
        event: 'question_displayed',
        workspaceId,
        metadata: { dimension, question_id: questionId, session_id: sessionId },
      });
    }
  }, [questionId, subphase, workspaceId, sessionId, sessionMeta]);

  // Track phase1_dropoff on unmount during Phase 1.1
  useEffect(() => {
    return () => {
      if (subphase === '1.1' && workspaceId && !hasSubmittedRef.current) {
        const timeOnPageMs = Date.now() - pageEnteredAtRef.current;
        logStartFlowEvent({
          event: 'phase1_dropoff',
          workspaceId,
          metadata: {
            dimension: sessionMeta?.onboarding_context?.dominant_dimension || 'unknown',
            question_id: questionId,
            time_on_page_ms: timeOnPageMs,
            word_count: thoughtContent.trim().split(/\s+/).filter(Boolean).length,
            session_id: sessionId,
          },
        });
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Autosave draft on every keystroke (debounced inside hook)
  useEffect(() => {
    if (thoughtContent.trim()) {
      saveDraft(thoughtContent, questionId);
    }
  }, [thoughtContent, saveDraft, questionId]);

  // Restore draft when subphase changes (e.g. after conflict reload)
  useEffect(() => {
    const draft = restoreDraft();
    if (draft?.text && !thoughtContent.trim()) {
      setThoughtContent(draft.text);
    }
  }, [subphase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Load complete session state on mount or when sessionId changes
  useEffect(() => {
    loadCompleteSessionState();
  }, [sessionId]);

  const loadCompleteSessionState = async () => {
    if (!sessionId) return;

    try {
      // Fetch the complete session data
      const { data: session, error: sessionError } = await supabase
        .from('decision_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();

      if (sessionError) {
        console.error('Error loading session:', sessionError);
        return;
      }

      if (!session) return;

      // Set phase and subphase from DB
      setPhase(session.phase);
      setSubphase(session.subphase);
      setProblemStatement(session.summary || session.initial_description || '');
      setWorkspaceId(session.workspace_id);
      setRowVersion(session.row_version || 0);
      
      // Set session context for dynamic placeholders in Phase 1.1
      setSessionContext({
        decision_type: session.decision_type || undefined,
        decision_subtype: session.decision_subtype || undefined,
        trigger: session.trigger || undefined,
        stakes: session.stakes || undefined,
      });
      
      // Parse onboarding context for Phase 1.1 CommitGate
      try {
        let triggerData: any = null;
        if (session.trigger) {
          try { triggerData = JSON.parse(session.trigger); } catch { triggerData = null; }
        }
        let stakesData: any = null;
        if (session.stakes) {
          try { stakesData = JSON.parse(session.stakes); } catch { stakesData = null; }
        }
        
        // Parse previous_attempts JSON for evidence block
        let previousAttemptsData: OnboardingContext['previousAttemptsData'] = undefined;
        if (session.previous_attempts) {
          try {
            const pa = JSON.parse(session.previous_attempts);
            if (pa && typeof pa === 'object' && !Array.isArray(pa)) {
              const checks: string[] = pa.checks || [];
              const signal: string = pa.signal || '';
              const blocker: string = pa.blocker || '';
              const blockerType: string = pa.blockerType || '';
              const learning: string = pa.learning || '';
              // Validation percent calculation (mirrors PreviousAttemptsStep logic)
              const TEST_WEIGHTS: Record<string, number> = { interviews: 2, willingness: 3, pilot: 3, built_unvalidated: 0, nothing: 0 };
              let score = checks.reduce((s: number, id: string) => s + (TEST_WEIGHTS[id] ?? 0), 0);
              if (signal.trim().length > 10) score += 2;
              if (blocker.trim().length > 10) score += 1;
              const validationPercent = score >= 8 ? 100 : score >= 5 ? 60 : score >= 2 ? 30 : 5;
              previousAttemptsData = { checks, learning, signal, blocker, blockerType, validationPercent };
            }
          } catch { /* not JSON, ignore */ }
        }

        // Read initial preference and impact level from meta
        const meta = session.meta as any;
        const initialPreference = meta?.initial_preference as 'a' | 'b' | 'undecided' | undefined;
        const computedImpactLevel = meta?.computed_impact_level as string | undefined;

        setOnboardingContext({
          optionA: triggerData?.optionA ? { label: triggerData.optionA.label || triggerData.optionA, description: triggerData.optionA.description || "" } : undefined,
          optionB: triggerData?.optionB ? { label: triggerData.optionB.label || triggerData.optionB, description: triggerData.optionB.description || "" } : undefined,
          worstCase: stakesData?.worst_case?.scenario || stakesData?.worstCase?.scenario || undefined,
          reversibility: stakesData?.reversibility || stakesData?.optionA?.reversibility || undefined,
          urgencyScore: session.urgency_score || undefined,
          impactScore: session.impact_score || undefined,
          dominantDimension: (session.meta as any)?.onboarding_context?.dominant_dimension || undefined,
          previousAttempts: session.previous_attempts || undefined,
          decisionScope: session.decision_scope || undefined,
          previousAttemptsData,
          initialPreference,
          computedImpactLevel,
        });
      } catch (e) {
        console.error('Error parsing onboarding context:', e);
      }

      // Set clarity score and kern status from meta
      if (session.meta) {
        const meta = session.meta as any;
        if (meta.last_clarity_score !== undefined) {
          setClarityScore(meta.last_clarity_score);
        }
        if (meta.kern_status) {
          setKernStatus(meta.kern_status);
        }
        if (meta.detected_tensions) {
          setDetectedTensions(meta.detected_tensions);
        }
        setSessionMeta(meta);

        // Retry loading thesis if missing (race condition: page loads before phase1-start finishes)
        if (!meta.generated_thesis && session.phase === 1 && session.subphase === '1.1') {
          setTimeout(async () => {
            try {
              const { data: refreshed } = await supabase
                .from('decision_sessions')
                .select('meta')
                .eq('id', sessionId)
                .single();
              if (refreshed?.meta) {
                const refreshedMeta = refreshed.meta as any;
                if (refreshedMeta.generated_thesis) {
                  setSessionMeta(refreshedMeta);
                }
              }
            } catch (e) {
              console.warn('Thesis retry failed:', e);
            }
          }, 2000);
        }

        // Load or generate dynamic placeholders
        if (meta.dynamic_placeholders?.examples) {
          setDynamicPlaceholders(meta.dynamic_placeholders.examples);
        } else if (session.phase === 1) {
          // Generate placeholders for Phase 1 if they don't exist
          generateDynamicPlaceholders(sessionId);
        }
      }

      // Fetch the last answered step to get the next question
      const { data: lastStep, error: stepError } = await supabase
        .from('decision_steps')
        .select('*')
        .eq('session_id', sessionId)
        .order('step_number', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (stepError) {
        console.error('Error loading last step:', stepError);
      }

      // Use the next question stored in session meta (from the last phase1-next response)
      if (session.meta) {
        const meta = session.meta as any;
        if (meta.next_question?.text) {
          setQuestion(meta.next_question.text);
          setParaphrase(meta.next_question.paraphrase || '');
          setQuestionId(meta.next_question.id || 'q_problem_initial');
          setCurrentInternalName(meta.next_question.internal_name || '');
          setFollowUpType(meta.next_question.follow_up_type || 'primary');
        } else if (location.state?.firstQuestion) {
          setQuestion(location.state.firstQuestion);
          setParaphrase(location.state.paraphrase || '');
          setQuestionId(location.state.questionId || 'q_problem_initial');
        } else {
          // Subphase-specific fallback questions
          const subphase = session.subphase || '1.1';
          if (subphase.startsWith('1.1')) {
            setQuestion("Was genau ist das Problem, das Sie lösen möchten?");
            setQuestionId('q_problem_initial');
          } else if (subphase.startsWith('1.2')) {
            setQuestion("Welche Optionen sehen Sie aktuell?");
            setQuestionId('q_options_initial');
          } else {
            setQuestion("Welche unbelegte Annahme hinter deinen Optionen würde ein konkreter Test in 14 Tagen widerlegen?");
            setQuestionId('q_hypothesis_initial');
          }
          setParaphrase('');
        }
      } else if (location.state?.firstQuestion) {
        setQuestion(location.state.firstQuestion);
        setParaphrase(location.state.paraphrase || '');
        setQuestionId(location.state.questionId || 'q_problem_initial');
      } else {
        // Same subphase-specific fallback for no-meta case
        setQuestion("Was genau ist das Problem, das Sie lösen möchten?");
        setParaphrase('');
        setQuestionId('q_problem_initial');
      }

      if (location.state?.analysis) {
        setAnalysis(location.state.analysis);
      }

      // Check if we need to redirect to a later phase
      if (session.phase === 2) {
        navigate(`/app/session/${sessionId}/phase2`);
        return;
      } else if (session.phase === 3) {
        // Subphase 3.4 = Report
        if (session.subphase === '3.4') {
          navigate(`/app/session/${sessionId}/report`);
        } else {
          navigate(`/app/session/${sessionId}/phase3`);
        }
        return;
      }
    } catch (error) {
      console.error('Error loading complete session state:', error);
    }
  };

  const generateDynamicPlaceholders = async (sessionId: string) => {
    try {
      console.log("Generating dynamic placeholders for session:", sessionId);
      const { data, error } = await supabase.functions.invoke('phase1-generate-placeholders', {
        body: { sessionId }
      });

      if (error) {
        console.error("Error generating placeholders:", error);
        return;
      }

      if (data?.placeholders) {
        setDynamicPlaceholders(data.placeholders);
        console.log("Dynamic placeholders loaded:", data.placeholders);
      }
    } catch (error) {
      console.error("Failed to generate placeholders:", error);
    }
  };

  // Scroll to top when subphase changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Broadcast current subphase to presence
    updatePresence({ current_subphase: subphase });
  }, [subphase, updatePresence]);

  // Typing indicator: broadcast is_typing on keystroke, debounce off after 2s
  useEffect(() => {
    if (!thoughtContent.trim()) return;
    updatePresence({ is_typing: true });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      updatePresence({ is_typing: false });
    }, 2000);
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [thoughtContent, updatePresence]);

  // Toast notification when another user submits a step
  useEffect(() => {
    if (!latestLiveStep) return;
    const { step, eventType } = latestLiveStep;
    // Only notify about other users' steps
    if (step.created_by === user?.id) {
      clearLatestLiveStep();
      return;
    }
    const name = step.creator_name || 'Ein Teilnehmer';
    const action = eventType === 'INSERT' ? 'hat Schritt' : 'hat Schritt bearbeitet:';
    toast({
      title: `${name} ${action} ${step.step_number}`,
      description: (step.question_text?.slice(0, 80) || '') + ((step.question_text?.length ?? 0) > 80 ? '…' : ''),
    });
    clearLatestLiveStep();
  }, [latestLiveStep, user?.id, toast, clearLatestLiveStep]);


  const checkPhaseRedirect = async () => {
    if (!sessionId) return;

    try {
      const { data: session, error } = await supabase
        .from('decision_sessions')
        .select('phase')
        .eq('id', sessionId)
        .single();

      if (!error && session?.phase === 2) {
        navigate(`/app/session/${sessionId}/phase2`);
      }
    } catch (error) {
      console.error('Error checking phase:', error);
    }
  };

  const loadSessionMetadata = async () => {
    if (!sessionId) return;
    
    try {
      const { data: session, error } = await supabase
        .from('decision_sessions')
        .select('meta')
        .eq('id', sessionId)
        .single();
      
      if (!error && session?.meta) {
        setSessionMeta(session.meta);
      }
    } catch (error) {
      console.error('Error loading session metadata:', error);
    }
  };



  // Helper function to get human-readable phase label
  const getPhaseLabel = (phase: number, subphase: string): string => {
    const labels: Record<string, string> = {
      "1.1": "Commit definieren",
      "1.2": "Spannungsfeld zuspitzen",
      "1.3": "Reversibilität klären",
      "1.4": "These bestätigen",
      "2.0": "Szenarien analysieren",
      "2.2": "Bewertung & Vergleich",
      "3.0": "Entscheidung & Report",
    };
    return labels[subphase] || `Phase ${phase}`;
  };

  // Helper function to get dynamic button text
  const getButtonText = (subphase: string): string => {
    const texts: Record<string, string> = {
      "1.1": "Weiter zur Analyse",
      "1.2": "Weiter zur nächsten Frage",
      "1.3": "Weiter zur nächsten Frage",
      "1.4": "These bestätigen & weiter",
    };
    return texts[subphase] || "Weiter";
  };

  // Helper function to get context-aware placeholder text
  const getPlaceholderText = (subphase: string, internalName: string, currentQuestion: string): string => {
    // Try to use dynamic placeholders first
    if (dynamicPlaceholders.length > 0) {
      const lowerQ = currentQuestion.toLowerCase();
      
      // Map questions to placeholder indices
      if (subphase === "1.1") {
        if (lowerQ.includes("wichtigste") && lowerQ.includes("schritt")) {
          return dynamicPlaceholders[0] || "z. B. «Den nächsten konkreten Schritt definieren.»";
        }
        if (lowerQ.includes("stakeholder") || lowerQ.includes("beteiligt")) {
          return dynamicPlaceholders[1] || "z. B. «Verschiedene Stakeholder mit unterschiedlichen Interessen.»";
        }
        if (lowerQ.includes("problem") || lowerQ.includes("spiel")) {
          return dynamicPlaceholders[2] || "z. B. «Die Glaubwürdigkeit und das Vertrauen stehen auf dem Spiel.»";
        }
        if (lowerQ.includes("kontext") || lowerQ.includes("hintergrund")) {
          return dynamicPlaceholders[3] || "z. B. «Der relevante Kontext für diese Entscheidung.»";
        }
        return dynamicPlaceholders[0] || "z. B. «Deine konkrete Situation beschreiben.»";
      }
      
      if (subphase === "1.2") {
        if (internalName === "tension_core" || lowerQ.includes("ziele") || lowerQ.includes("konkurrieren")) {
          return dynamicPlaceholders[4] || "z. B. «Zwei konkurrierende Ziele oder Interessen.»";
        }
        return dynamicPlaceholders[4] || "z. B. «Die zentrale Spannung beschreiben.»";
      }
      
      if (subphase === "1.3") {
        if (internalName === "hypothesis_core" || lowerQ.includes("annahme")) {
          return dynamicPlaceholders[5] || "z. B. «Meine zentrale Annahme ist...»";
        }
        if (internalName === "hypothesis_risk" || lowerQ.includes("worst") || lowerQ.includes("falsch")) {
          return dynamicPlaceholders[6] || "z. B. «Falls diese Annahme falsch ist...»";
        }
        if (internalName === "hypothesis_primary") {
          return dynamicPlaceholders[7] || "z. B. «Meine Arbeitshypothese lautet...»";
        }
        return dynamicPlaceholders[5] || "z. B. «Deine Hypothese formulieren.»";
      }
      
      if (subphase === "1.4") {
        return dynamicPlaceholders[7] || "z. B. «Die zentrale Entscheidungsfrage formulieren.»";
      }
    }
    
    // Fallback to static placeholders
    const lowerQ = currentQuestion.toLowerCase();
    
    // Subphase 1.1 - Problem verstehen
    if (subphase === "1.1") {
      if (lowerQ.includes("wichtigste") && lowerQ.includes("schritt")) {
        return "z. B. «Den nächsten konkreten Meilenstein definieren und Verantwortlichkeiten klären.»";
      }
      if (lowerQ.includes("stakeholder") || lowerQ.includes("beteiligt")) {
        return "z. B. «Das Kernteam, die Geschäftsführung und wichtige Partner haben unterschiedliche Erwartungen.»";
      }
      if (lowerQ.includes("problem") || lowerQ.includes("spiel")) {
        return "z. B. «Die Glaubwürdigkeit gegenüber wichtigen Stakeholdern steht auf dem Spiel.»";
      }
      if (lowerQ.includes("kontext") || lowerQ.includes("hintergrund")) {
        return "z. B. «Wir befinden uns in einer kritischen Phase mit mehreren konkurrierenden Prioritäten.»";
      }
      return "z. B. «Die aktuelle Situation konkret beschreiben und den Handlungsbedarf erklären.»";
    }
    
    // Subphase 1.2 - Spannungsfeld
    if (subphase === "1.2") {
      if (internalName === "tension_core") {
        if (lowerQ.includes("ziele") || lowerQ.includes("konkurrieren")) {
          return "z. B. «Schnelles Wachstum vs. nachhaltige Entwicklung – oder: Kurzfristige Erfolge vs. langfristige Stabilität.»";
        }
        return "Beschreibe die Ziele, die miteinander konkurrieren – z. B. Innovation vs. Stabilität, Fokus vs. Breite, kurz- vs. langfristig.";
      } else if (internalName === "tension_sharpen") {
        return "z. B. «Kurzfristige Umsatzziele erreichen vs. langfristige Positionierung aufbauen.»";
      } else if (internalName === "tension_primary") {
        return "z. B. «Der Kernkonflikt ist: Sofort handeln und Risiken eingehen – oder erst absichern und Zeit verlieren?»";
      }
    }
    
    // Subphase 1.3 - Hypothesen
    if (subphase === "1.3") {
      if (internalName === "hypothesis_core") {
        return "z. B. «Ich gehe davon aus, dass wir mit den vorhandenen Ressourcen und gezielter Priorisierung das Ziel erreichen können.»";
      } else if (internalName === "hypothesis_risk") {
        return "z. B. «Falls diese Annahme falsch ist, müssen wir zusätzliche Ressourcen mobilisieren oder das Ziel anpassen.»";
      } else if (internalName === "hypothesis_primary") {
        return "z. B. «Mit fokussierter Umsetzung können wir die kritischen Meilensteine rechtzeitig erreichen.»";
      }
    }
    
    // Subphase 1.4 - Verdichtung
    if (subphase === "1.4") {
      if (internalName === "synthesis_decision_question") {
        return "z. B. «Sollen wir kurzfristig Ressourcen in X investieren (und Risiko Y eingehen) – oder erst Y absichern (und Chance X riskieren)?»";
      } else if (internalName === "synthesis_hypothesis_confirm") {
        return "z. B. «Ja, das trifft es: Wir können uns fokussierte Umsetzung leisten, wenn wir gleichzeitig kritische Risiken adressieren.»";
      } else if (internalName === "synthesis_success_criteria") {
        return "z. B. «Erfolg = Messbare Verbesserung in X + Stabilität in Y + positive Resonanz der Stakeholder. Zeitrahmen: 3-6 Monate.»";
      }
    }
    
    // Default
    return "Schreibe hier deine Gedanken auf. Je konkreter, desto besser kann ich dir helfen.";
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    // Validate files
    const validFiles = files.filter(file => {
      const maxSize = 10 * 1024 * 1024; // 10 MB
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'image/png',
        'image/jpeg',
        'image/jpg'
      ];
      
      if (file.size > maxSize) {
        toast({
          title: "Datei zu groß",
          description: `${file.name} ist größer als 10 MB.`,
          variant: "destructive",
        });
        return false;
      }
      
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Dateityp nicht unterstützt",
          description: `${file.name} hat einen nicht unterstützten Dateityp.`,
          variant: "destructive",
        });
        return false;
      }
      
      return true;
    });
    
    setAttachments(prev => [...prev, ...validFiles]);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);

      toast({
        title: "Aufnahme gestartet",
        description: "Sprich jetzt deine Gedanken aus",
      });
    } catch (error) {
      console.error('Error starting recording:', error);
      toast({
        title: "Fehler",
        description: "Mikrofon-Zugriff wurde verweigert",
        variant: "destructive",
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob: Blob) => {
    setIsTranscribing(true);
    
    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = (reader.result as string).split(',')[1];
        
        const { data, error } = await supabase.functions.invoke('transcribe-audio', {
          body: { audio: base64Audio }
        });

        if (error) {
          console.error('Transcription error:', error);
          throw error;
        }

        if (data?.text) {
          // Append transcribed text to existing content
          setThoughtContent(prev => {
            const separator = prev.trim() ? ' ' : '';
            return prev + separator + data.text;
          });

          toast({
            title: "Transkription erfolgreich",
            description: "Der Text wurde hinzugefügt",
          });
        }
      };
    } catch (error) {
      console.error('Error transcribing audio:', error);
      toast({
        title: "Fehler bei der Transkription",
        description: "Die Aufnahme konnte nicht in Text umgewandelt werden",
        variant: "destructive",
      });
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleVoiceInput = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const uploadAttachments = async (sessionId: string, stepId?: string) => {
    if (attachments.length === 0) return [];

    const uploadedFiles: Array<{ file_name: string; file_path: string; file_type: string; file_size: number }> = [];

    for (const file of attachments) {
      try {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${user?.id}/${sessionId}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('session-attachments')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        // Save metadata to database
        const { error: dbError } = await supabase
          .from('session_attachments')
          .insert({
            session_id: sessionId,
            step_id: stepId,
            file_name: file.name,
            file_path: filePath,
            file_type: file.type,
            file_size: file.size,
            uploaded_by: user?.id,
          });

        if (dbError) throw dbError;

        uploadedFiles.push({
          file_name: file.name,
          file_path: filePath,
          file_type: file.type,
          file_size: file.size,
        });
      } catch (error) {
        console.error('Error uploading file:', error);
        toast({
          title: "Fehler beim Hochladen",
          description: `${file.name} konnte nicht hochgeladen werden.`,
          variant: "destructive",
        });
      }
    }

    return uploadedFiles;
  };

  const handleStepClick = (step: any) => {
    setQuestion(step.question_text);
    setQuestionId(step.question_id);
    setThoughtContent(step.user_answer || "");
    setEditingStepNumber(step.step_number);
    setPhase(step.phase);
    setSubphase(step.subphase);
    setCurrentInternalName(step.internal_name || "");
    setIsHistoryOpen(false);
    setAttachments([]); // Clear attachments when editing a step
    
    // No deletion warning - data is preserved until new answer is submitted
  };

  // Go back to the previous answered question
  const handleGoBack = () => {
    if (steps.length === 0) {
      toast({
        title: "Keine vorherigen Antworten",
        description: "Du bist am Anfang der Session.",
      });
      return;
    }

    // Get the last step
    const lastStep = steps[steps.length - 1];
    handleStepClick(lastStep);
  };

  // Navigate to a specific subphase by finding the last step in that subphase
  const handleSubphaseNavigation = (targetSubphase: string) => {
    // Find the last step in the target subphase
    const stepsInSubphase = steps.filter(s => s.subphase === targetSubphase);
    
    if (stepsInSubphase.length === 0) {
      toast({
        title: "Keine Fragen in dieser Phase",
        description: "Diese Phase enthält noch keine beantworteten Fragen.",
      });
      return;
    }

    // Get the last step from that subphase
    const lastStepInSubphase = stepsInSubphase[stepsInSubphase.length - 1];
    handleStepClick(lastStepInSubphase);
  };

  const handleSubmit = async () => {
    if (!thoughtContent.trim() || !sessionId) {
      toast({
        title: "Antwort erforderlich",
        description: "Bitte gib eine Antwort ein, bevor du fortfährst.",
        variant: "destructive",
      });
      return;
    }

    if (thoughtContent.trim().length < 10) {
      toast({
        title: "Antwort zu kurz",
        description: "Bitte gib eine ausführlichere Antwort (mindestens 10 Zeichen).",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setSaveStatus('saving');

    // Telemetry: track Phase 1.1 answer events
    if (subphase === '1.1' && workspaceId) {
      const timeOnPageMs = Date.now() - pageEnteredAtRef.current;
      const wordCount = thoughtContent.trim().split(/\s+/).filter(Boolean).length;
      const dimension = sessionMeta?.onboarding_context?.dominant_dimension || 'unknown';
      const isRewrite = hasSubmittedRef.current;
      logStartFlowEvent({
        event: isRewrite ? 'phase1_answer_rewrite' : 'phase1_first_answer',
        workspaceId,
        metadata: { dimension, question_id: questionId, word_count: wordCount, time_on_page_ms: timeOnPageMs, session_id: sessionId },
      });
      hasSubmittedRef.current = true;
    }

    try {
      // Upload attachments first if any
      let uploadedFiles: any[] = [];
      if (attachments.length > 0) {
        setUploadingFiles(true);
        uploadedFiles = await uploadAttachments(sessionId);
        setUploadingFiles(false);
      }

      // If editing a previous step, delete all subsequent steps first
      if (editingStepNumber !== null) {
        const { error: deleteError } = await supabase
          .from('decision_steps')
          .delete()
          .eq('session_id', sessionId)
          .gt('step_number', editingStepNumber);

        if (deleteError) {
          console.error('Error deleting subsequent steps:', deleteError);
          throw new Error("Fehler beim Löschen nachfolgender Schritte");
        }
      }

      // Fetch fresh row_version to avoid conflicts after phase1-start updates
      const { data: freshSession } = await supabase
        .from('decision_sessions')
        .select('row_version')
        .eq('id', sessionId)
        .single();
      const currentRowVersion = freshSession?.row_version ?? rowVersion;
      setRowVersion(currentRowVersion);

      const { data, error } = await supabase.functions.invoke('phase1-next', {
        body: {
          session_id: sessionId,
          question_id: questionId,
          user_answer: thoughtContent.trim(),
          row_version: currentRowVersion,
          client_subphase: subphase,
          attachments: uploadedFiles.length > 0 ? uploadedFiles : undefined,
          // Include structured data for Phase 1.1 to store in meta
          structured_input: subphase === "1.1" && structuredInputData ? {
            thesis_text: structuredInputData.thesis_text,
            killer_condition: structuredInputData.killer_condition,
          } : undefined,
          // Include Phase 1.2 conflict data
          conflict_input: subphase === "1.2" && conflictInputData ? {
            capacity_percent: conflictInputData.capacityPercent,
            kpi_metric: conflictInputData.kpiMetric,
            kpi_from: conflictInputData.kpiFrom,
            kpi_to: conflictInputData.kpiTo,
            impact_timeframe: conflictInputData.impactTimeframe,
            tension_score: conflictInputData.tensionScore,
            risk_alignment_score: conflictInputData.riskAlignmentScore,
            risk_type: conflictInputData.riskType,
          } : undefined,
          conflict_cost_input: subphase === "1.2" && conflictCostData ? {
            delay_months: conflictCostData.delayMonths,
            monthly_cost: conflictCostData.monthlyCost,
            total_cost: conflictCostData.totalCost,
            wasted_capacity_months: conflictCostData.wastedCapacityMonths,
            conversion_rate: conflictCostData.conversionRate,
            avg_unit_value: conflictCostData.avgUnitValue,
            commitment_decision: conflictCostData.commitmentDecision,
          } : undefined,
          // Include Phase 1.4 killer assumption data
          killer_assumption_input: subphase === "1.4" && killerAssumptionData ? {
            killer_assumption: killerAssumptionData.killerAssumption,
          } : undefined,
          // Include Phase 1.3 structured irreversibility data
          reversibility_input: subphase === "1.3" && phase13Data ? {
            validation_metric: phase13Data.validationMetric,
            validation_threshold: phase13Data.validationThreshold,
            validation_deadline_days: phase13Data.validationDeadlineDays,
            pivot_dev_months: phase13Data.pivotDevMonths,
            pivot_calendar_months: phase13Data.pivotCalendarMonths,
            pivot_cost_eur: phase13Data.pivotCostEur,
            exposure_until_validation: phase13Data.exposureUntilValidation,
            exposure_until_pivot: phase13Data.exposureUntilPivot,
            commit_decision: phase13Data.commitDecision,
            expected_exposure: phase13Data.expectedExposure,
            adjusted_pivot_cost: phase13Data.adjustedPivotCost,
            daily_exposure: phase13Data.dailyExposure,
            lock_in_types: phase13Data.lockInTypes,
            lock_in_concrete_step: phase13Data.lockInConcreteStep,
            structural_urgency_score: phase13Data.structuralUrgencyScore,
            structural_urgency_level: phase13Data.structuralUrgencyLevel,
          } : undefined,
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        // Check if this is a row_version conflict (409)
        const errorMsg = error.message || '';
        if (errorMsg.includes('row_version conflict') || (data && data.error === 'row_version conflict')) {
          throw new Error('row_version conflict: ' + (data?.details || 'Session wurde zwischenzeitlich geändert'));
        }
        throw new Error(errorMsg || "Fehler beim Verarbeiten der Antwort");
      }

      // Also check for conflict in data (edge function may return error in body)
      if (data?.error === 'row_version conflict') {
        throw new Error('row_version conflict: ' + (data.details || 'Session wurde zwischenzeitlich geändert'));
      }

      if (!data) {
        throw new Error("Keine Antwort vom Server erhalten");
      }

      if (data) {
        // Check for subphase transitions
        const isSubphaseTransition = subphase !== data.subphase;
        const isPhaseTransition = phase !== data.phase;
        
        // Update all state with the response
        // Note: edge function returns next_question object, not flat fields
        const nextQuestion = data.next_question || {};
        setQuestion(nextQuestion.text || data.next_question_text || "");
        setQuestionId(nextQuestion.id || data.next_question_id || "");
        setPhase(data.phase);
        setSubphase(data.subphase);
        setClarityScore(data.clarity_score);
        setKernStatus(data.kern_status);
        setDetectedTensions(data.detected_tensions || []);
        setProblemStatement(data.problem_statement || data.updated_problem_statement || "");
        setCurrentInternalName(nextQuestion.internal_name || data.next_internal_name || "");
        setFollowUpType(nextQuestion.follow_up_type || 'primary');
        setAnsweredInSubphase(data.answered_in_subphase || 0);
        setTotalInSubphase(data.total_in_subphase || 3);
        setNextSubphase(data.suggested_next_subphase || "");
        if (data.row_version !== undefined) setRowVersion(data.row_version);
        
        // Reload session metadata for 1.4 context
        loadSessionMetadata();
        
        // Show transition dialog for significant transitions
        if (isSubphaseTransition || isPhaseTransition) {
          // Special case: Phase 1.4 → Phase 2.0 gets full-screen transition
          const isPhase2Transition = 
            (subphase === "1.4" && data.subphase === "2.0") ||
            (phase === 1 && data.phase === 2);
          
          if (isPhase2Transition) {
            // Show the dedicated Phase 2 transition screen
            setTransitionData({
              fromSubphase: subphase,
              toSubphase: data.subphase,
              tensions: data.detected_tensions || [],
              problemStatement: data.updated_problem_statement,
              fromPhase: phase,
              toPhase: data.phase,
            });
            setShowPhase2Transition(true);
            return; // Don't continue - Phase2Transition handles navigation
          }
          
          // Regular subphase transitions within Phase 1
          const shouldShowDialog = 
            (subphase === "1.1" && data.subphase === "1.2") ||
            (subphase === "1.2" && data.subphase === "1.3") ||
            (subphase === "1.3" && data.subphase === "1.4");
          
          if (shouldShowDialog) {
            setTransitionData({
              fromSubphase: subphase,
              toSubphase: data.subphase,
              tensions: data.detected_tensions || [],
              problemStatement: data.updated_problem_statement,
              fromPhase: phase,
              toPhase: data.phase,
            });
            setShowTransitionDialog(true);
          }
        }
        
        
        // Clear the textarea, attachments, structured input, editing state and draft
        setThoughtContent("");
        clearDraft();
        setStructuredInputData(null);
        setAttachments([]);
        setEditingStepNumber(null);

        // Broadcast last_step_at and stop typing
        updatePresence({ last_step_at: new Date().toISOString(), is_typing: false });

        // Show saved status briefly
        setSaveStatus('saved');

        // Clear any existing timeout
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        // Reset to idle after 3 seconds
        saveTimeoutRef.current = setTimeout(() => {
          setSaveStatus('idle');
        }, 3000);

        // Show transition message if applicable
        if (data.phase_transition?.transitioned) {
          toast({
            title: "Phasenübergang",
            description: data.phase_transition.message,
          });
        }
        
        // Note: Phase 2 navigation is handled by Phase2Transition component
      }
    } catch (error: any) {
      console.error('Error submitting answer:', error);
      setSaveStatus('idle');

      const msg = error.message || '';
      const isConflict = msg.includes('row_version') || msg.includes('conflict') || msg.includes('modified');
      const isRLS = msg.includes('row-level security') || msg.includes('policy');
      const isJWT = msg.includes('JWT');

      if (isConflict) {
        // Conflict: another user edited while you were typing
        toast({
          title: "Konflikt erkannt",
          description: (
            <div className="space-y-2">
              <p>Jemand hat diesen Schritt zwischenzeitlich geändert. Dein Text wurde lokal gespeichert.</p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={async () => {
                    const ok = await copyDraftToClipboard();
                    if (ok) {
                      toast({ title: "Kopiert!", description: "Dein Text ist in der Zwischenablage." });
                    }
                  }}
                >
                  Kopieren
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => window.location.reload()}
                >
                  Neu laden
                </Button>
              </div>
            </div>
          ),
          variant: "destructive",
          duration: 15000,
        });
      } else if (isJWT) {
        toast({
          title: "Sitzung abgelaufen",
          description: (
            <div className="space-y-2">
              <p>Dein Text wurde gespeichert. Bitte lade die Seite neu.</p>
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => window.location.reload()}>
                Neu laden
              </Button>
            </div>
          ),
          variant: "destructive",
          duration: 10000,
        });
      } else if (isRLS) {
        toast({
          title: "Keine Berechtigung",
          description: "Du darfst diesen Schritt nicht bearbeiten. Nur der Owner oder der ursprüngliche Autor (Contributor) kann ihn ändern.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Fehler",
          description: msg || "Es gab ein Problem beim Verarbeiten deiner Antwort.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Global phase navigation handlers
  const handleGlobalPhaseNavigation = (targetPhase: number) => {
    setTargetGlobalPhase(targetPhase);
    setShowGlobalPhaseDialog(true);
  };

  const handleConfirmGlobalNavigation = async () => {
    if (!sessionId || targetGlobalPhase === null) return;
    
    try {
      if (targetGlobalPhase === 2) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 2, subphase: "2.0" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}/phase2`);
      } else if (targetGlobalPhase === 3) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 3, subphase: "3.0" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}/phase3`);
      } else if (targetGlobalPhase === 4) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 3, subphase: "3.4" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}/report`);
      }
    } catch (error: any) {
      console.error("Error navigating to phase:", error);
      toast({
        title: "Fehler",
        description: "Navigation fehlgeschlagen.",
        variant: "destructive",
      });
    }
  };

  const getGlobalPhaseLabel = (phase: number) => {
    const labels: Record<number, string> = {
      1: "Phase 1 – Analyse",
      2: "Phase 2 – Optionen",
      3: "Phase 3 – Bewertung",
      4: "Report"
    };
    return labels[phase] || `Phase ${phase}`;
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div 
          className="absolute top-0 left-0 w-full h-full opacity-[0.02]"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 60% 40% at 10% 20%, hsl(var(--primary)) 0%, transparent 50%),
              radial-gradient(ellipse 50% 50% at 90% 80%, hsl(var(--primary)) 0%, transparent 50%)
            `,
          }}
        />
        <div 
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="container flex h-16 items-center justify-between max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 md:gap-4">
            <img 
              src={quentariLogo} 
              alt="Quentari" 
              className="h-8 w-auto cursor-pointer transition-transform duration-300 hover:scale-105" 
              onClick={() => navigate('/dashboard')}
            />
            <div className="h-6 w-px bg-border hidden sm:block" />
            <GlobalPhaseTimeline
              currentPhase={1}
              sessionId={sessionId!}
              onPhaseClick={handleGlobalPhaseNavigation}
            />
            <div className="hidden lg:flex items-center gap-2 ml-2">
              <SaveStatusIndicator status={saveStatus} />
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <CollaborationBar users={activeUsers} steps={steps} currentSubphase={subphase} />
            {/* Participants Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsParticipantsOpen(true)}
              className="gap-2"
            >
              <Users className="h-4 w-4" />
              Teilnehmer
            </Button>
            
            {/* Comments Button */}
            {sessionId && workspaceId && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsCommentsOpen(true)}
                className="gap-2"
              >
                <MessageSquare className="h-4 w-4" />
                Kommentare
              </Button>
            )}
            
            <Sheet open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <History className="h-4 w-4" />
                  Historie
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-[400px] md:w-[540px]">
                <SessionHistory 
                  steps={steps} 
                  isLoading={isLoadingSteps}
                  onStepClick={handleStepClick}
                  myRole={myRole}
                  onStepSeen={markStepSeen}
                />
              </SheetContent>
            </Sheet>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/onboarding")}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Neue Session
            </Button>
            <UserMenu />
          </div>

          {/* Mobile Navigation */}
          <div className="flex md:hidden items-center gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-primary/10 hover:text-primary">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] border-border/50">
                <div className="flex flex-col gap-6 mt-6">
                  <div className="p-4 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
                    <span className="text-sm font-medium">Phasen-Navigation</span>
                    <div className="mt-3">
                      <GlobalPhaseTimeline
                        currentPhase={1}
                        sessionId={sessionId!}
                        onPhaseClick={handleGlobalPhaseNavigation}
                      />
                    </div>
                    <div className="mt-2">
                      <SaveStatusIndicator status={saveStatus} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <span className="text-sm font-medium">Aktive Nutzer</span>
                    <CollaborationBar users={activeUsers} steps={steps} currentSubphase={subphase} />
                  </div>
                  <div className="flex flex-col gap-3 border-t border-border/50 pt-4">
                    
                    {/* Participants Button - Mobile */}
                    <Button
                      variant="ghost"
                      className="justify-start gap-2 hover:bg-primary/10 hover:text-primary"
                      onClick={() => setIsParticipantsOpen(true)}
                    >
                      <Users className="h-5 w-5" />
                      <span>Teilnehmer</span>
                    </Button>
                    
                    {/* Comments Button - Mobile */}
                    {sessionId && workspaceId && (
                      <Button
                        variant="ghost"
                        className="justify-start gap-2 hover:bg-primary/10 hover:text-primary"
                        onClick={() => setIsCommentsOpen(true)}
                      >
                        <MessageSquare className="h-5 w-5" />
                        <span>Kommentare</span>
                      </Button>
                    )}
                    
                    <Button
                      variant="ghost"
                      className="justify-start gap-2 hover:bg-primary/10 hover:text-primary"
                      onClick={() => setIsHistoryOpen(true)}
                    >
                      <History className="h-5 w-5" />
                      <span>Historie</span>
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate("/dashboard")}
                      className="w-full gap-2 border-primary/20 hover:bg-primary/5 hover:border-primary/30"
                    >
                      <Plus className="h-4 w-4" />
                      Neue Session
                    </Button>
                  </div>
                  <div className="border-t border-border/50 pt-4">
                    <UserMenu />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="container max-w-7xl mx-auto px-4 md:px-6 pt-16 pb-24 md:pb-40 relative z-10">
        {phase === 2 ? (
          <ScenarioBuilder 
            sessionId={sessionId!} 
            onContinue={() => navigate(`/app/session/${sessionId}/phase2`)}
          />
        ) : (
          <>
            {/* Minimal Phase Indicator */}
            <HorizontalPhaseIndicator 
              currentSubphase={subphase}
              clarityScore={clarityScore}
              answeredInSubphase={answeredInSubphase}
              totalInSubphase={totalInSubphase}
              onSubphaseClick={handleSubphaseNavigation}
            />

            {/* HAUPTFOKUS: Die Frage - ganz oben, groß, klar */}
            <div className="mb-6 animate-fade-in max-w-3xl mx-auto pt-4">
              {/* Back button row */}
              {steps.length > 0 && subphase !== "1.1" && (
                <button
                  onClick={handleGoBack}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors group"
                >
                  <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
                  <span>Zurück zur letzten Antwort</span>
                </button>
              )}

              {/* ═══ Startfrage-Anker: persistent ab 1.2 als Nordstern ═══ */}
              {subphase !== "1.1" && (() => {
                const startQuestion = sessionMeta?.onboarding_context?.customized_question_text
                  || steps?.[0]?.question_text
                  || sessionMeta?.next_question?.text
                  || '';
                if (!startQuestion) return null;
                const SUBPHASE_LENS: Record<string, string> = {
                  "1.2": "Beantworte diese Frage jetzt durch reale Kapazitätszuweisung.",
                  "1.3": "Identifiziere die Kernannahme, an der diese Frage steht oder fällt.",
                  "1.4": "Verdichte deine Analyse zu einer belastbaren These.",
                };
                return (
                  <div className="mb-5 max-w-[720px] relative overflow-hidden rounded-xl border border-primary/20 bg-primary/[0.03]">
                    <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-primary to-primary/30 rounded-full" />
                    <div className="p-4 pl-5">
                      <p className="text-[10px] font-bold text-primary uppercase tracking-widest mb-2">
                        Kernfrage dieser Analyse
                      </p>
                      <p className="text-base md:text-lg font-semibold text-foreground leading-relaxed">
                        {startQuestion.replace(/\*\*/g, '')}
                      </p>
                      {SUBPHASE_LENS[subphase] && (
                        <p className="text-sm text-muted-foreground mt-2">
                          {SUBPHASE_LENS[subphase]}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}
              
              <h1 className="text-xl md:text-2xl lg:text-[28px] leading-[1.35] tracking-tight font-semibold text-foreground max-w-[720px]">
                {subphase === "1.1" ? "Konflikt operationalisieren" : question.replace(/\*\*/g, '')}
              </h1>
              
              {/* Ultrakurzes Ziel */}
              <p className="text-sm text-muted-foreground mt-2 max-w-[720px]">
                {subphase === "1.1" && "Alle folgenden Schritte schärfen oder widerlegen diese Frage."}
                {subphase === "1.2" && "Mache Opportunitätskosten sichtbar. Keine Theorie, sondern Zahlen."}
                {subphase === "1.3" && "Ab welchem Punkt gibt es kein Zurück?"}
                {subphase === "1.4" && "Bestätige deine Entscheidungs-These."}
              </p>

              {/* ═══ Konflikt-Anker: Algorithmische Startfrage (nur 1.1) ═══ */}
              {subphase === "1.1" && question && (
                <div className="mt-5 max-w-[720px] relative overflow-hidden rounded-xl border border-primary/20 bg-primary/[0.03]">
                  <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-primary to-primary/30 rounded-full" />
                  <div className="p-4 pl-5">
                    <p className="text-[10px] font-bold text-primary uppercase tracking-widest mb-2">
                      Kernfrage dieser Analyse
                    </p>
                    <p className="text-base md:text-lg font-semibold text-foreground leading-relaxed">
                      {question.replace(/\*\*/g, '')}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Editing Warning - nur wenn relevant */}
            {editingStepNumber !== null && (
              <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg max-w-[720px] mx-auto">
                <div className="flex items-start gap-2">
                  <RefreshCw className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                      Du bearbeitest Schritt {editingStepNumber}
                    </p>
                    <p className="text-xs text-amber-600/80 dark:text-amber-400/80 mt-0.5">
                      Nachfolgende Schritte könnten beim Absenden aktualisiert werden, basierend auf deiner neuen Antwort.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Problem-Statement komplett entfernt aus Phase 1 - Nutzer soll denken, nicht lesen */}
            
            {/* Phase 1.4 Entscheidungsrahmen - nur Zielkonflikt + Hypothese, KEIN Problemkern */}
            {subphase === "1.4" && (detectedTensions.length > 0 || sessionMeta?.primary_hypothesis) && (
              <Collapsible defaultOpen={false} className="mb-4 max-w-[720px] mx-auto">
                <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full group p-2.5 bg-muted/20 border border-border/30 rounded-lg">
                  <Layout className="w-3.5 h-3.5" />
                  <span className="font-medium">Bisherige Erkenntnisse</span>
                  <span className="text-xs opacity-60 ml-1">(zur Referenz)</span>
                  <ChevronDown className="w-4 h-4 ml-auto transition-transform group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <div className="p-3 bg-muted/30 border border-border/30 rounded-lg space-y-2">
                    {detectedTensions.length > 0 && (
                      <p className="text-sm text-foreground/80">
                        <span className="font-medium text-foreground">Zielkonflikt:</span> {detectedTensions[0]}
                      </p>
                    )}
                    {sessionMeta?.primary_hypothesis && (
                      <p className="text-sm text-foreground/80">
                        <span className="font-medium text-foreground">Kernhypothese:</span> {sessionMeta.primary_hypothesis}
                      </p>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Kernhypothese Vorschlag - nur wenn relevant */}
            {currentInternalName === "synthesis_hypothesis_confirm" && sessionMeta?.primary_hypothesis && (
              <div className="mb-4 p-3 bg-primary/5 border border-primary/20 rounded-lg max-w-[720px] mx-auto">
                <div className="flex items-start gap-2">
                  <FileCheck className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground font-medium mb-1">Kernhypothese (Vorschlag):</p>
                    <p className="text-sm text-foreground">{sessionMeta.primary_hypothesis}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Decision Framework Summary entfernt - Duplikat mit Collapsible oben */}

            {/* Denkstütze now integrated into Phase13HypothesisInput */}

            {/* Leitfrage für 1.4 now integrated into Phase14KillerAssumptionInput */}

            <div className="animate-fade-in max-w-3xl mx-auto" style={{ animationDelay: "0.1s" }}>
              {/* Phase 1.1 PRIMARY: Strukturierte Eingabe */}
              {subphase === "1.1" && followUpType === 'primary' ? (
                <div
                  className="w-full p-5 md:p-8 bg-gradient-to-b from-[#FAFAF7] to-[#F5F5F0] dark:from-card dark:to-card/95 border border-[hsl(var(--workspace-border))]/60 rounded-[20px] shadow-[0_4px_20px_rgba(0,0,0,0.08),0_8px_40px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.02]"
                >
                  <Phase11CommitGate 
                    onValueChange={setThoughtContent}
                    onArtifactsChange={setStructuredInputData}
                    onGateStatusChange={setCommitGateValid}
                    onboardingContext={onboardingContext}
                    generatedThesis={sessionMeta?.generated_thesis}
                  />
                </div>
              ) : subphase === "1.2" && followUpType === 'primary' ? (
                /* Phase 1.2 PRIMARY: Konflikt-Karte mit zwei Polen */
                <div
                  className="w-full p-5 md:p-8 bg-gradient-to-b from-[#FAFAF7] to-[#F5F5F0] dark:from-card dark:to-card/95 border border-[hsl(var(--workspace-border))]/60 rounded-[20px] shadow-[0_4px_20px_rgba(0,0,0,0.08),0_8px_40px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.02]"
                >
                  <Phase12ConflictInput 
                    onValueChange={setThoughtContent}
                    onStructuredChange={setConflictInputData}
                    onGateStatusChange={setConflictGateValid}
                    optionALabel={onboardingContext?.optionA?.label}
                    optionBLabel={onboardingContext?.optionB?.label}
                    thesisText={sessionMeta?.generated_thesis?.thesis_text || sessionMeta?.phase1_1_artifacts?.thesis_text}
                    killerCondition={sessionMeta?.phase1_1_artifacts?.killer_condition}
                    dominantRisk={sessionMeta?.generated_thesis?.dominant_risk}
                    blocker={onboardingContext?.previousAttemptsData?.blocker}
                    blockerType={onboardingContext?.previousAttemptsData?.blockerType}
                    validationPercent={onboardingContext?.previousAttemptsData?.validationPercent}
                    strongestSignal={onboardingContext?.previousAttemptsData?.signal}
                    initialPreference={onboardingContext?.initialPreference}
                    computedImpactLevel={onboardingContext?.computedImpactLevel}
                    urgencyScore={onboardingContext?.urgencyScore}
                    worstCase={onboardingContext?.worstCase}
                    reversibility={onboardingContext?.reversibility}
                    kippunktWindow={(() => {
                      const stakes = (sessionMeta?.onboarding_context?.structured_stakes) as any;
                      if (!stakes) return undefined;
                      return {
                        timeframeMonths: Math.min(
                          stakes?.optionA?.timeframe_months || 12,
                          stakes?.optionB?.timeframe_months || 12
                        ),
                        worstCaseProbability: stakes?.worst_case?.probability,
                      };
                    })()}
                    onboardingMetric={sessionMeta?.onboarding_context?.priority_context?.metric}
                  />
                </div>
              ) : subphase === "1.2" && followUpType === 'escalate' ? (
                /* Phase 1.2 ESCALATE: Kosten der Nicht-Option */
                <div
                  className="w-full p-5 md:p-8 bg-gradient-to-b from-[#FAFAF7] to-[#F5F5F0] dark:from-card dark:to-card/95 border border-[hsl(var(--workspace-border))]/60 rounded-[20px] shadow-[0_4px_20px_rgba(0,0,0,0.08),0_8px_40px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.02]"
                >
                  <Phase12EscalateInput 
                    onValueChange={setThoughtContent}
                    onStructuredChange={setConflictCostData}
                    onGateStatusChange={setEscalateGateValid}
                    capacityPercent={conflictInputData?.capacityPercent ?? sessionMeta?.phase1_2_conflict?.capacity_percent}
                    kpiMetric={conflictInputData?.kpiMetric ?? sessionMeta?.phase1_2_conflict?.kpi_metric}
                    kpiFrom={conflictInputData?.kpiFrom ?? sessionMeta?.phase1_2_conflict?.kpi_from}
                    kpiTo={conflictInputData?.kpiTo ?? sessionMeta?.phase1_2_conflict?.kpi_to}
                    impactTimeframe={conflictInputData?.impactTimeframe ?? sessionMeta?.phase1_2_conflict?.impact_timeframe}
                    optionALabel={onboardingContext?.optionA?.label}
                    optionBLabel={onboardingContext?.optionB?.label}
                    worstCase={onboardingContext?.worstCase}
                    worstCaseProbability={(() => {
                      const stakes = sessionMeta?.onboarding_context?.structured_stakes as any;
                      return stakes?.worst_case?.probability || stakes?.worstCase?.probability || undefined;
                    })()}
                    reversibilityA={(() => {
                      const stakes = sessionMeta?.onboarding_context?.structured_stakes as any;
                      return stakes?.optionA?.reversibility || stakes?.reversibility || onboardingContext?.reversibility || undefined;
                    })()}
                    impactScore={onboardingContext?.impactScore}
                    urgencyScore={onboardingContext?.urgencyScore}
                  />
                </div>
              ) : subphase === "1.3" ? (
                /* Phase 1.3: Irreversibilitäts-Simulation */
                <div
                  className="w-full p-5 md:p-8 bg-gradient-to-b from-[#FAFAF7] to-[#F5F5F0] dark:from-card dark:to-card/95 border border-[hsl(var(--workspace-border))]/60 rounded-[20px] shadow-[0_4px_20px_rgba(0,0,0,0.08),0_8px_40px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.02]"
                >
                  <Phase13IrreversibilityEngine
                    onValueChange={setThoughtContent}
                    onStructuredChange={setPhase13Data}
                    onGateStatusChange={setPhase13GateValid}
                    thesisText={sessionMeta?.generated_thesis?.thesis_text || sessionMeta?.phase1_1_artifacts?.thesis_text}
                    killerCondition={sessionMeta?.phase1_1_artifacts?.killer_condition}
                    capacityPercent={conflictInputData?.capacityPercent ?? sessionMeta?.phase1_2_conflict?.capacity_percent}
                    kpiMetric={conflictInputData?.kpiMetric ?? sessionMeta?.phase1_2_conflict?.kpi_metric}
                    kpiFrom={conflictInputData?.kpiFrom ?? sessionMeta?.phase1_2_conflict?.kpi_from}
                    kpiTo={conflictInputData?.kpiTo ?? sessionMeta?.phase1_2_conflict?.kpi_to}
                    monthlyCost={conflictCostData?.monthlyCost ?? sessionMeta?.inaction_costs?.monthly_cost}
                    totalCost={conflictCostData?.totalCost ?? sessionMeta?.inaction_costs?.total_cost}
                    wastedCapacityMonths={conflictCostData?.wastedCapacityMonths ?? sessionMeta?.inaction_costs?.wasted_capacity_months}
                    teamCostPerMonth={conflictCostData?.avgUnitValue ? undefined : 8000}
                    commitmentDecision={conflictCostData?.commitmentDecision ?? sessionMeta?.inaction_costs?.commitment_decision}
                    optionALabel={onboardingContext?.optionA?.label}
                    optionBLabel={onboardingContext?.optionB?.label}
                    worstCase={onboardingContext?.worstCase}
                    reversibility={onboardingContext?.reversibility}
                    worstCaseProbability={(() => {
                      const stakes = sessionMeta?.onboarding_context?.structured_stakes as any;
                      return stakes?.worst_case?.probability || stakes?.worstCase?.probability || undefined;
                    })()}
                    urgencyScore={onboardingContext?.urgencyScore}
                  />
                </div>
              ) : subphase === "1.4" ? (
                /* Phase 1.4: Decision Thesis Rendering + Confirmation */
                <div
                  className="w-full p-5 md:p-8 bg-gradient-to-b from-[#FAFAF7] to-[#F5F5F0] dark:from-card dark:to-card/95 border border-[hsl(var(--workspace-border))]/60 rounded-[20px] shadow-[0_4px_20px_rgba(0,0,0,0.08),0_8px_40px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.02]"
                >
                  <Phase14DecisionThesis 
                    onValueChange={setThoughtContent}
                    thesisData={{
                      kernsatz: sessionMeta?.generated_thesis?.thesis_text || sessionMeta?.phase1_1_artifacts?.thesis_text,
                      killCriterion: sessionMeta?.phase1_1_artifacts?.killer_condition,
                      messsignal: sessionMeta?.generated_thesis?.expected_outcome,
                      deadline: sessionMeta?.generated_thesis?.timeframe_days ? String(sessionMeta.generated_thesis.timeframe_days) : undefined,
                      validator: undefined,
                      reversibilityMarker: sessionMeta?.reversibility_marker,
                      tensionDelta: {
                        inactionCostMonthly: sessionMeta?.inaction_costs?.monthly_cost,
                        inactionCostTotal: sessionMeta?.inaction_costs?.total_cost,
                        commitmentDecision: sessionMeta?.inaction_costs?.commitment_decision,
                        wastedCapacity: sessionMeta?.inaction_costs?.wasted_capacity_months,
                      },
                      irreversibility: sessionMeta?.phase1_3_irreversibility ? {
                        validationMetric: sessionMeta.phase1_3_irreversibility.validation_metric,
                        validationThreshold: sessionMeta.phase1_3_irreversibility.validation_threshold,
                        validationDeadlineDays: sessionMeta.phase1_3_irreversibility.validation_deadline_days,
                        pivotCalendarMonths: sessionMeta.phase1_3_irreversibility.pivot_calendar_months,
                        pivotCostEur: sessionMeta.phase1_3_irreversibility.pivot_cost_eur,
                        commitDecision: sessionMeta.phase1_3_irreversibility.commit_decision,
                        lockInTypes: sessionMeta.phase1_3_irreversibility.lock_in_types,
                        lockInConcreteStep: sessionMeta.phase1_3_irreversibility.lock_in_concrete_step,
                        structuralUrgencyScore: sessionMeta.phase1_3_irreversibility.structural_urgency_score,
                        structuralUrgencyLevel: sessionMeta.phase1_3_irreversibility.structural_urgency_level,
                        expectedExposure: sessionMeta.phase1_3_irreversibility.expected_exposure,
                        adjustedPivotCost: sessionMeta.phase1_3_irreversibility.adjusted_pivot_cost,
                        dailyExposure: sessionMeta.phase1_3_irreversibility.daily_exposure,
                        pivotDevMonths: sessionMeta.phase1_3_irreversibility.pivot_dev_months,
                        exposureUntilValidation: sessionMeta.phase1_3_irreversibility.exposure_until_validation,
                        exposureUntilPivot: sessionMeta.phase1_3_irreversibility.exposure_until_pivot,
                      } : undefined,
                      optionALabel: onboardingContext?.optionA?.label,
                      optionBLabel: onboardingContext?.optionB?.label,
                      worstCase: onboardingContext?.worstCase,
                      reversibility: onboardingContext?.reversibility,
                      urgencyScore: onboardingContext?.urgencyScore,
                      capacityPercent: sessionMeta?.phase1_2_conflict?.capacity_percent,
                      kpiMetric: sessionMeta?.phase1_2_conflict?.kpi_metric,
                      kpiFrom: sessionMeta?.phase1_2_conflict?.kpi_from,
                      kpiTo: sessionMeta?.phase1_2_conflict?.kpi_to,
                    }}
                  />
                </div>
              ) : (
                /* Fallback: Textarea für unerwartete Subphasen */
                <div
                  className="w-full min-h-[180px] md:min-h-[220px] p-5 md:p-8 bg-gradient-to-b from-[#FAFAF7] to-[#F5F5F0] dark:from-card dark:to-card/95 border border-[hsl(var(--workspace-border))]/60 rounded-[20px] shadow-[0_4px_20px_rgba(0,0,0,0.08),0_8px_40px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.02] transition-all duration-300 hover:border-[hsl(var(--workspace-border))]/70 cursor-text focus-within:shadow-[0_5px_24px_rgba(0,0,0,0.12),0_10px_45px_rgba(0,0,0,0.06)]"
                  onClick={(e) => {
                    const textarea = e.currentTarget.querySelector("textarea");
                    textarea?.focus();
                  }}
                >
                  <textarea
                    value={thoughtContent}
                    onChange={(e) => setThoughtContent(e.target.value)}
                    placeholder="Deine Antwort..."
                    className="w-full h-full min-h-[80px] md:min-h-[100px] bg-transparent border-0 outline-none resize-none text-[hsl(var(--workspace-text))] dark:text-foreground placeholder:text-muted-foreground/40 placeholder:italic text-[17px] leading-[1.7]"
                    style={{
                      fontFamily: "inherit",
                    }}
                  />

                  {/* File attachments display */}
                  {attachments.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {attachments.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-background border border-border rounded-lg"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Paperclip className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <span className="text-sm text-foreground truncate">
                              {file.name}
                            </span>
                            <span className="text-xs text-muted-foreground flex-shrink-0">
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <button
                            onClick={() => handleRemoveAttachment(index)}
                            className="p-1 hover:bg-destructive/10 rounded transition-colors"
                            aria-label="Datei entfernen"
                          >
                            <X className="w-4 h-4 text-destructive" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    multiple
                    accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                    className="hidden"
                  />

                  <div className="flex items-center gap-4 mt-4 justify-end">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isRecording || isTranscribing}
                            className="flex items-center gap-2 text-[#4B4F58] opacity-85 hover:opacity-100 hover:text-foreground hover:scale-[1.04] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="Dateien hochladen"
                            type="button"
                          >
                            <Paperclip className="w-5 h-5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Dokument anhängen (PDF, Word, Bilder)</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={handleVoiceInput}
                            disabled={isTranscribing}
                            className={`flex items-center gap-2 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${
                              isRecording 
                                ? 'text-destructive opacity-100 scale-110 animate-pulse' 
                                : 'text-[#4B4F58] opacity-85 hover:opacity-100 hover:text-foreground hover:scale-[1.04]'
                            }`}
                            aria-label={isRecording ? "Aufnahme stoppen" : "Spracheingabe"}
                            type="button"
                          >
                            <Mic className="w-5 h-5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Spracheingabe starten</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    {isTranscribing && (
                      <span className="text-xs text-muted-foreground animate-pulse">
                        Transkribiere...
                      </span>
                    )}
                  </div>
                  
                  {/* Live Quality Indicator */}
                  <div className="mt-3 flex justify-end">
                    <ResponseQualityIndicator 
                      text={thoughtContent} 
                      subphase={subphase} 
                    />
                  </div>
                </div>
              )}
            </div>

            <div
              className="mt-10 mb-8 flex justify-center animate-fade-in max-w-3xl mx-auto"
              style={{ animationDelay: "0.3s" }}
            >
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !thoughtContent.trim() || (subphase === '1.1' && !commitGateValid) || (subphase === '1.2' && followUpType === 'primary' && !conflictGateValid) || (subphase === '1.2' && followUpType === 'escalate' && !escalateGateValid) || (subphase === '1.3' && !phase13GateValid)}
                className={cn(
                  "h-14 px-12 text-base font-semibold shadow-lg transition-all duration-200",
                  (!thoughtContent.trim() || (subphase === '1.1' && !commitGateValid) || (subphase === '1.2' && followUpType === 'primary' && !conflictGateValid) || (subphase === '1.2' && followUpType === 'escalate' && !escalateGateValid) || (subphase === '1.3' && !phase13GateValid))
                    ? "bg-muted/80 text-muted-foreground hover:bg-muted" 
                    : "bg-primary hover:bg-primary/90 hover:shadow-xl hover:scale-[1.02]"
                )}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {uploadingFiles ? 'Lade Dateien hoch...' : 'Verarbeite...'}
                  </>
                ) : (
                  getButtonText(subphase)
                )}
              </Button>
            </div>
          </>
        )}
      </main>

      {/* Participants Panel */}
      {sessionId && (
        <ParticipantsPanel
          sessionId={sessionId}
          workspaceId={workspaceId}
          open={isParticipantsOpen}
          onOpenChange={setIsParticipantsOpen}
        />
      )}

      {/* Comments Sheet - wird über Header-Button geöffnet */}
      {sessionId && workspaceId && (
        <Sheet open={isCommentsOpen} onOpenChange={setIsCommentsOpen}>
          <SheetContent side="right" className="w-full sm:max-w-[480px] md:max-w-[540px] p-0 flex flex-col">
            <div className="p-4 border-b border-border bg-muted/30 flex-shrink-0">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold">Kommentare & Aktivitäten</h2>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Diskutiere mit deinem Team über diese Entscheidung
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-6 min-h-0">
              <SessionComments sessionId={sessionId} workspaceId={workspaceId} />
              <div className="border-t border-border pt-4">
                <SessionActivityTimeline sessionId={sessionId} workspaceId={workspaceId} />
              </div>
            </div>
          </SheetContent>
        </Sheet>
      )}
      
      {/* Subphase Transition Dialog */}
      {transitionData && (
        <SubphaseTransitionDialog
          open={showTransitionDialog}
          onOpenChange={setShowTransitionDialog}
          fromSubphase={transitionData.fromSubphase}
          toSubphase={transitionData.toSubphase}
          detectedTensions={transitionData.tensions}
          problemStatement={transitionData.problemStatement}
          phase={transitionData.fromPhase}
          nextPhase={transitionData.toPhase}
        />
      )}

      {/* Phase 2 Full-Screen Transition */}
      {showPhase2Transition && sessionId && (
        <Phase2Transition
          sessionId={sessionId}
          problemStatement={transitionData?.problemStatement || problemStatement}
          tensions={transitionData?.tensions || detectedTensions}
          primaryHypothesis={sessionMeta?.primary_hypothesis}
          decisionQuestion={sessionMeta?.decision_question}
          sessionMeta={sessionMeta}
          onError={(message) => {
            setShowPhase2Transition(false);
            toast({
              title: "Fehler",
              description: message,
              variant: "destructive",
            });
          }}
        />
      )}

      {/* Global Phase Navigation Dialog */}
      <PhaseNavigationDialog
        open={showGlobalPhaseDialog}
        onOpenChange={setShowGlobalPhaseDialog}
        onConfirm={handleConfirmGlobalNavigation}
        fromPhase="Phase 1 – Analyse"
        toPhase={getGlobalPhaseLabel(targetGlobalPhase || 2)}
      />
    </div>
  );
};

export default Session;
